import { request } from '../utils/request';
import type {
  CreateCustomerFeedbackRequest,
  CreateCustomerFeedbackResponse,
  CustomerFeedbackResponse,
} from '@/types/customer-feedback.types';
import { submitCustomerFeedbackOffline } from '../services/offline-surveys.service';

export const createCustomerFeedback = async (
  data: CreateCustomerFeedbackRequest,
): Promise<CreateCustomerFeedbackResponse> => {
  // If offline, save to IndexedDB
  if (!navigator.onLine) {
    const localId = await submitCustomerFeedbackOffline(data);
    return {
      message:
        'Customer feedback saved offline. It will be submitted when you are back online.',
      uuid: localId,
    };
  }

  // Online: submit to server
  return request<CreateCustomerFeedbackResponse>(
    '/surveys/customer-feedbacks',
    {
      method: 'POST',
      body: data,
    },
  );
};

export const getCustomerFeedbackByUuid = async (
  uuid: string,
): Promise<CustomerFeedbackResponse> => {
  return request<CustomerFeedbackResponse>(
    `/surveys/customer-feedbacks/${uuid}`,
    { method: 'GET' },
  );
};

export const markCustomerFeedbackAsViewed = async (
  uuid: string,
): Promise<{ message: string }> => {
  return request<{ message: string }>(
    `/surveys/customer-feedbacks/${uuid}/viewed`,
    { method: 'PATCH' },
  );
};

export const markCustomerFeedbackAsUnviewed = async (
  uuid: string,
): Promise<{ message: string }> => {
  return request<{ message: string }>(
    `/surveys/customer-feedbacks/${uuid}/unviewed`,
    { method: 'PATCH' },
  );
};

export const downloadCustomerFeedbackPdf = async (
  uuid: string,
): Promise<void> => {
  const { blob, filename } = await request<{
    blob: Blob;
    filename: string | null;
  }>(`/surveys/customer-feedbacks/${uuid}/download`, {
    responseType: 'blob',
    returnFilename: true,
  });

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename ?? 'CustomerFeedback.pdf';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
